import { useForm } from "@tanstack/react-form";
import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { Button } from "../../components/ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../../components/ui/dialog";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "../../components/ui/field";
import { Input } from "../../components/ui/input";

export default function AddTenantDomainDialog({
	tenantId,
	open,
	onOpenChange,
}: {
	tenantId: Id<"tenants">;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const addTenantDomain = useMutation(api.staff.addTenantDomain);

	const form = useForm({
		defaultValues: { domain: "" },
		onSubmit: async ({ value }) => {
			if (!value.domain.trim()) return;
			mutate({ domain: value.domain.trim() });
		},
	});

	const { mutate, isPending, error } = useReactQueryMutation({
		mutationFn: async (args: { domain: string }) => {
			return await addTenantDomain({ tenantId, domain: args.domain });
		},
		onSuccess: () => {
			form.reset();
			onOpenChange(false);
		},
	});

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Add email domain</DialogTitle>
					<DialogDescription>
						Users who sign in with an email at this domain and have no
						pre-registered record will be auto-added to this firm as a member.
					</DialogDescription>
				</DialogHeader>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						form.handleSubmit();
					}}
				>
					<FieldGroup>
						<form.Field name="domain">
							{(field) => (
								<Field>
									<FieldLabel htmlFor="tenant-domain">Domain</FieldLabel>
									<Input
										id="tenant-domain"
										placeholder="acme.com"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
										required
										autoFocus
									/>
									<FieldDescription>
										Enter the bare domain without the @ (e.g. acme.com).
										Personal-email domains like gmail.com aren't allowed.
									</FieldDescription>
								</Field>
							)}
						</form.Field>
						{error ? (
							<Field>
								<p className="text-sm text-destructive">
									{error instanceof Error ? error.message : String(error)}
								</p>
							</Field>
						) : null}
						<DialogFooter>
							<DialogClose render={<Button variant="outline" type="button" />}>
								Cancel
							</DialogClose>
							<form.Subscribe selector={(state) => [state.values.domain]}>
								{([domain]) => (
									<Button
										type="submit"
										disabled={isPending || !(domain as string).trim()}
									>
										{isPending ? "Adding..." : "Add domain"}
									</Button>
								)}
							</form.Subscribe>
						</DialogFooter>
					</FieldGroup>
				</form>
			</DialogContent>
		</Dialog>
	);
}
